/**
 * `/d/:department` — the report list for one department (SPEC §9.1).
 *
 * Title, summary, and owner per §9.1. "Last viewed" is deliberately absent:
 * it needs a per-user read of the audit log, which is an M4 endpoint. Showing
 * a plausible-looking placeholder for it would be worse than leaving it out.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { api } from '../lib/api'
import { ErrorState, Skeleton } from '../components/States'
import { useMe } from '../lib/session'

export function Department() {
  const { department = '' } = useParams()
  const me = useMe()
  const { data, error, isPending } = useQuery({
    queryKey: ['department-reports', department],
    queryFn: () => api.departmentReports(department),
  })

  const name = me?.departments.find((entry) => entry.key === department)?.name ?? department

  if (isPending) return <Skeleton rows={3} />
  // A department the caller cannot reach 404s, byte-identical to one that does
  // not exist (§7.2). The message says the same either way.
  if (error !== null) return <ErrorState error={error} />

  return (
    <div>
      <nav className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
        <Link to="/" className="hover:text-[var(--color-ink)]">
          Departments
        </Link>
        <span aria-hidden> / </span>
        <span>{name}</span>
      </nav>

      <h1 className="mt-1 flex items-center gap-2 text-[length:var(--text-lg)] font-semibold tracking-tight">
        <span aria-hidden className="h-5 w-1 rounded-full bg-[var(--accent)]" />
        {name}
      </h1>

      <ul className="mt-6 space-y-2">
        {data.map((report) => (
          <li key={report.slug}>
            <Link
              to={`/d/${department}/r/${report.slug}`}
              className="block rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-4 elevated hover:border-[var(--color-ink-muted)]"
            >
              <p className="text-[length:var(--text-base)] font-medium">{report.title}</p>
              <p className="mt-0.5 text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
                {report.summary}
              </p>
              <p className="mt-2 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
                Owner: {report.owner}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
